import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as WhatsAppIcon } from 'assets/icons/ui-elements/small/whatsapp_square_sm.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/green-circle-white-check.svg';
import { ReactComponent as CheckGIcon } from 'assets/icons/check-g.svg';
import { ReactComponent as XIConRIcon } from 'assets/icons/x-icon-r.svg';
import { ReactComponent as ExclamationOIcon } from 'assets/icons/exclamation-o.svg';
import { ReactComponent as ExclamationBIcon } from 'assets/icons/exclamation-b.svg';
import { ReactComponent as HourglassOIcon } from 'assets/icons/hourglass-o.svg';
import { format, parseISO } from 'date-fns';
import { AgendaTarefa } from 'services/reports/endpoints/AgendaService';
import {
  ActivityListWrapper,
  TableWrapper,
  Table,
  Thead,
  Th,
  Tr,
  Td,
  ExecutorList,
  ExecutorRow,
  ExecutorAvatar,
  ExecutorAvatarPlaceholder,
  WhatsAppLink,
  WhatsAppExecutorName,
  StatusChip,
  RowActionsCell,
  KebabButton,
  RowMenuWrapper,
  PortalRowMenu,
  RowMenuItem,
  DesktopOnly,
  MobileOnly,
  MobileCardList,
  MobileCard,
  MobileCardTopRow,
  MobileCardTitle,
  MobileCardTitleBold,
  MobileCardFields,
  MobileCardField,
  MobileCardFieldLabel,
  MobileCardFieldValue,
  MobileCardExecutors,
  MobileCardExecutorLabel,
} from './styled';

const buildAvatarUrl = (fotoPath: string | null): string => {
  if (!fotoPath) return '';
  const base = (process.env.REACT_APP_URL_IMAGES || '').replace(/\/$/, '');
  const path = fotoPath.startsWith('/') ? fotoPath : `/${fotoPath}`;
  return `${base}${path}`;
};

const getInitials = (nome: string): string => {
  const parts = nome.trim().split(' ');
  if (parts.length === 1) return parts[0][0] ?? '';
  return `${parts[0][0]}${parts[parts.length - 1][0]}`;
};

interface ActivityListProps {
  tarefas: AgendaTarefa[];
  isCompletedView: boolean;
  canDelete: boolean;
  canRestore: boolean;
  onRequestExcluir: (tarefa: AgendaTarefa) => void;
  onRequestRestaurar?: (tarefa: AgendaTarefa) => void;
}

const formatDate = (iso: string | null): string => {
  if (!iso) return '-';
  try {
    return format(parseISO(iso), 'dd/MM/yyyy');
  } catch {
    return '-';
  }
};

const formatTime = (iso: string | null): string => {
  if (!iso) return '-';
  try {
    return format(parseISO(iso), 'HH:mm');
  } catch {
    return '-';
  }
};

const buildWhatsAppUrl = (telefone: string): string => {
  const digits = telefone.replace(/\D/g, '');
  const number = digits.startsWith('55') ? digits : `55${digits}`;
  return `https://api.whatsapp.com/send?l=pt_BR&phone=${number}`;
};

const getStatusVariant = (tarefa: AgendaTarefa): 'green' | 'blue' | 'red' => {
  switch (tarefa.status) {
    case 3:
    case 5:
      return 'green';
    case 4:
      return 'red';
    case 2:
      return 'blue';
    default: // status 1 (Pendente)
      try {
        if (tarefa.dataPrevista && parseISO(tarefa.dataPrevista) < new Date()) return 'red';
      } catch {}
      return 'blue';
  }
};

const getStatusLabel = (tarefa: AgendaTarefa): string => {
  switch (tarefa.status) {
    case 2:
      return 'Aguardando aprovação';
    case 3:
      return 'Realizada';
    case 4:
      return 'Não realizada';
    case 5:
      return 'Baixada automaticamente';
    default: // status 1 (Pendente)
      try {
        if (tarefa.dataPrevista && parseISO(tarefa.dataPrevista) < new Date()) return 'Atrasada';
      } catch {}
      return 'Agendada';
  }
};

const getStatusIcon = (tarefa: AgendaTarefa): React.ReactElement => {
  switch (tarefa.status) {
    case 3:
      return <CheckGIcon width={20} height={20} style={{ flexShrink: 0 }} />;
    case 4:
      return <XIConRIcon width={20} height={20} style={{ flexShrink: 0 }} />;
    case 5:
      return <CheckIcon width={20} height={20} style={{ flexShrink: 0 }} />;
    case 2:
      return <HourglassOIcon width={20} height={20} style={{ flexShrink: 0 }} />;
    default: // status 1 (Pendente)
      try {
        if (tarefa.dataPrevista && parseISO(tarefa.dataPrevista) < new Date())
          return <ExclamationOIcon width={20} height={20} style={{ flexShrink: 0 }} />;
      } catch {}
      return <ExclamationBIcon width={20} height={20} style={{ flexShrink: 0 }} />;
  }
};

const KebabIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="3" r="1.5" />
    <circle cx="8" cy="8" r="1.5" />
    <circle cx="8" cy="13" r="1.5" />
  </svg>
);

export const ActivityList: React.FC<ActivityListProps> = ({
  tarefas,
  isCompletedView,
  canDelete,
  canRestore,
  onRequestExcluir,
  onRequestRestaurar,
}) => {
  const showKebab = canDelete || (isCompletedView && canRestore);

  type MenuPos = { top?: number; bottom?: number; right: number };
  const [openMenuRow, setOpenMenuRow] = useState<number | null>(null);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const portalMenuRef = useRef<HTMLUListElement>(null);

  const closeMenu = () => {
    setOpenMenuRow(null);
    setMenuPos(null);
  };

  const handleKebabClick = (e: React.MouseEvent<HTMLButtonElement>, tarefaNumero: number) => {
    if (openMenuRow === tarefaNumero) {
      closeMenu();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const upward = vh - rect.bottom < 120;
    setMenuPos(
      upward ? { bottom: vh - rect.top, right: vw - rect.right } : { top: rect.bottom, right: vw - rect.right },
    );
    setOpenMenuRow(tarefaNumero);
  };

  useEffect(() => {
    if (openMenuRow === null) return;
    const handleOutside = (e: MouseEvent) => {
      if (portalMenuRef.current && !portalMenuRef.current.contains(e.target as Node)) closeMenu();
    };
    const handleScroll = () => closeMenu();
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [openMenuRow]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderMenuItems = (tarefa: AgendaTarefa) => (
    <>
      {isCompletedView && canRestore && (
        <RowMenuItem
          restore
          onClick={() => {
            closeMenu();
            onRequestRestaurar?.(tarefa);
          }}
        >
          Restaurar
        </RowMenuItem>
      )}
      {canDelete && (
        <RowMenuItem
          danger
          onClick={() => {
            closeMenu();
            onRequestExcluir(tarefa);
          }}
        >
          Excluir
        </RowMenuItem>
      )}
    </>
  );

  const wrapperStyle = isCompletedView ? { paddingTop: '16px' } : undefined;

  if (tarefas.length === 0) {
    return (
      <ActivityListWrapper style={wrapperStyle}>
        <p style={{ padding: '24px', color: '#888', fontFamily: 'Roboto', fontSize: '14px' }}>
          Nenhuma atividade encontrada.
        </p>
      </ActivityListWrapper>
    );
  }

  return (
    <ActivityListWrapper style={wrapperStyle}>
      <DesktopOnly>
        <TableWrapper>
          <Table>
            <Thead>
              <tr>
                <Th>Nº</Th>
                <Th>Descrição</Th>
                <Th>Data</Th>
                <Th>Horário</Th>
                <Th>Executores</Th>
                <Th>Status</Th>
                {showKebab && <Th />}
              </tr>
            </Thead>
            <tbody>
              {tarefas.map(tarefa => {
                const dataIso = isCompletedView ? tarefa.dataRealizada : tarefa.dataPrevista;
                const isMenuOpen = openMenuRow === tarefa.numero;
                return (
                  <Tr key={tarefa.numero}>
                    <Td>{tarefa.numero}</Td>
                    <Td>{tarefa.descricao}</Td>
                    <Td style={{ whiteSpace: 'nowrap' }}>{formatDate(dataIso)}</Td>
                    <Td style={{ whiteSpace: 'nowrap' }}>{formatTime(dataIso)}</Td>
                    <Td>
                      <ExecutorList>
                        {tarefa.executores.map((exec, i) => {
                          const avatarUrl = buildAvatarUrl(exec.fotoPath);
                          return (
                            <ExecutorRow key={`${exec.nome}-${i}`}>
                              {avatarUrl ? (
                                <ExecutorAvatar src={avatarUrl} alt={exec.nome} />
                              ) : (
                                <ExecutorAvatarPlaceholder>{getInitials(exec.nome)}</ExecutorAvatarPlaceholder>
                              )}
                              {exec.telefone ? (
                                <WhatsAppLink
                                  href={buildWhatsAppUrl(exec.telefone)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`WhatsApp: ${exec.telefone}`}
                                >
                                  <WhatsAppIcon />
                                  <WhatsAppExecutorName>{exec.nome}</WhatsAppExecutorName>
                                </WhatsAppLink>
                              ) : (
                                <span>{exec.nome}</span>
                              )}
                            </ExecutorRow>
                          );
                        })}
                      </ExecutorList>
                    </Td>
                    <Td>
                      {(() => {
                        const variant = getStatusVariant(tarefa);
                        return (
                          <StatusChip variant={variant}>
                            {getStatusIcon(tarefa)}
                            {getStatusLabel(tarefa)}
                          </StatusChip>
                        );
                      })()}
                    </Td>
                    {showKebab && (
                      <RowActionsCell>
                        <RowMenuWrapper>
                          <KebabButton
                            type="button"
                            aria-label="Ações"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleKebabClick(e, tarefa.numero)}
                          >
                            <KebabIcon />
                          </KebabButton>
                        </RowMenuWrapper>
                        {isMenuOpen &&
                          menuPos &&
                          ReactDOM.createPortal(
                            <PortalRowMenu ref={portalMenuRef} style={menuPos}>
                              {renderMenuItems(tarefa)}
                            </PortalRowMenu>,
                            document.body,
                          )}
                      </RowActionsCell>
                    )}
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </DesktopOnly>

      <MobileOnly>
        <MobileCardList>
          {tarefas.map((tarefa, index) => {
            const dataIso = isCompletedView ? tarefa.dataRealizada : tarefa.dataPrevista;
            const isMenuOpen = openMenuRow === tarefa.numero;
            const variant = getStatusVariant(tarefa);
            return (
              <MobileCard key={tarefa.numero} even={index % 2 === 0}>
                <MobileCardTopRow>
                  <StatusChip variant={variant}>
                    {getStatusIcon(tarefa)}
                    {getStatusLabel(tarefa)}
                  </StatusChip>
                  {showKebab && (
                    <>
                      <RowMenuWrapper>
                        <KebabButton
                          type="button"
                          aria-label="Ações"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleKebabClick(e, tarefa.numero)}
                        >
                          <KebabIcon />
                        </KebabButton>
                      </RowMenuWrapper>
                      {isMenuOpen &&
                        menuPos &&
                        ReactDOM.createPortal(
                          <PortalRowMenu ref={portalMenuRef} style={menuPos}>
                            {renderMenuItems(tarefa)}
                          </PortalRowMenu>,
                          document.body,
                        )}
                    </>
                  )}
                </MobileCardTopRow>

                <MobileCardTitle>
                  <MobileCardTitleBold>Nº {tarefa.numero}:</MobileCardTitleBold> {tarefa.descricao}
                </MobileCardTitle>

                <MobileCardFields>
                  <MobileCardField>
                    <MobileCardFieldLabel>Data:</MobileCardFieldLabel>
                    <MobileCardFieldValue>{formatDate(dataIso)}</MobileCardFieldValue>
                  </MobileCardField>
                  <MobileCardField>
                    <MobileCardFieldLabel>Horário:</MobileCardFieldLabel>
                    <MobileCardFieldValue>{formatTime(dataIso)}</MobileCardFieldValue>
                  </MobileCardField>
                </MobileCardFields>

                {tarefa.executores.length > 0 && (
                  <MobileCardExecutors>
                    <MobileCardExecutorLabel>
                      Executor{tarefa.executores.length > 1 ? 'es' : ''}:
                    </MobileCardExecutorLabel>
                    <ExecutorList>
                      {tarefa.executores.map((exec, i) => {
                        const avatarUrl = buildAvatarUrl(exec.fotoPath);
                        return (
                          <ExecutorRow key={`${exec.nome}-${i}`}>
                            {avatarUrl ? (
                              <ExecutorAvatar src={avatarUrl} alt={exec.nome} />
                            ) : (
                              <ExecutorAvatarPlaceholder>{getInitials(exec.nome)}</ExecutorAvatarPlaceholder>
                            )}
                            {exec.telefone ? (
                              <WhatsAppLink
                                href={buildWhatsAppUrl(exec.telefone)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`WhatsApp: ${exec.telefone}`}
                              >
                                <WhatsAppIcon />
                                <WhatsAppExecutorName>{exec.nome}</WhatsAppExecutorName>
                              </WhatsAppLink>
                            ) : (
                              <span style={{ color: '#7f746a' }}>{exec.nome}</span>
                            )}
                          </ExecutorRow>
                        );
                      })}
                    </ExecutorList>
                  </MobileCardExecutors>
                )}
              </MobileCard>
            );
          })}
        </MobileCardList>
      </MobileOnly>
    </ActivityListWrapper>
  );
};
